import { Bell, BellOff, CheckCheck, Compass, FolderPlus, LogOut, PenLine, PickRoles, Plus, Settings, Stoke, UserCircle, UserPlus } from '@/components/ui/icons';
import { createContext, useContext, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { BLAZE_NAME, serverPerks, STOKE_LEVELS, type NotificationLevel } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { serverApi, userApi } from '@/api/endpoints';
import { Logo } from '@/components/Brand';
import { CountBadge, ServerIcon } from '@/components/ui/Avatar';
import { Menu, Tooltip, type MenuEntry } from '@/components/ui/Popover';
import { dragKindOf, isAfter, reorderIds, startDrag } from '@/lib/dragReorder';
import { useMyPermissions } from '@/lib/permissions';
import { acknowledge, isChannelUnread, useDmUnreadTotal, useServerUnread } from '@/lib/readState';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { hasBlazeNow } from '@/stores/blaze';
import { useServers } from '@/stores/servers';
import { useFriends, useReadStates } from '@/stores/social';
import { confirm, toast, useUi } from '@/stores/ui';

/** Down the left (the default), or across the top (a layout option on bigger screens). */
const Horizontal = createContext(false);

function Pill({ state }: { state: 'active' | 'unread' | 'none' }) {
  const horizontal = useContext(Horizontal);
  if (horizontal) {
    return (
      <span
        aria-hidden
        className={cn(
          'absolute bottom-0 left-1/2 h-[5px] -translate-x-1/2 rounded-t-full bg-rail-fg transition-all duration-200',
          state === 'active' ? 'w-9' : state === 'unread' ? 'w-2' : 'w-0 group-hover:w-5',
        )}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        'absolute top-1/2 left-0 w-[5px] -translate-y-1/2 rounded-r-full bg-rail-fg transition-all duration-200',
        state === 'active' ? 'h-9' : state === 'unread' ? 'h-2' : 'h-0 group-hover:h-5',
      )}
    />
  );
}

function RailSlot({ children, pill }: { children: ReactNode; pill: 'active' | 'unread' | 'none' }) {
  const horizontal = useContext(Horizontal);
  return (
    <div className={cn('group relative flex justify-center', horizontal ? 'h-full items-center px-1' : 'w-full')}>
      <Pill state={pill} />
      {children}
    </div>
  );
}

export function ServerRail({ activeServerId, horizontal = false }: { activeServerId?: string; horizontal?: boolean }) {
  const tooltip = horizontal ? 'bottom' : 'right';
  const order = useServers((s) => s.order);
  const openModal = useUi((s) => s.openModal);
  const dmUnread = useDmUnreadTotal();
  const pending = useFriends((s) => s.pendingCount);
  const isHome = !activeServerId;
  const [dragId, setDragId] = useState<string | null>(null);
  const [spot, setSpot] = useState<{ id: string; after: boolean } | null>(null);

  /** Your own server order: optimistic, synced to your other tabs by the server. */
  const moveServer = async (id: string, targetId: string, after: boolean) => {
    const previous = useServers.getState().order;
    const next = reorderIds(previous, id, targetId, after);
    if (next.join() === previous.join()) return;
    useServers.getState().setOrder(next);
    try {
      await userApi.setServerOrder(next);
    } catch (err) {
      useServers.getState().setOrder(previous);
      toast.error(errorMessage(err, 'Could not reorder your servers.'));
    }
  };

  const dropProps = (id: string) => ({
    onDragOver: (e: DragEvent) => {
      if (dragKindOf(e) !== 'server') return;
      e.preventDefault();
      const after = isAfter(e, horizontal ? 'x' : 'y');
      setSpot((s) => (s?.id === id && s.after === after ? s : { id, after }));
    },
    onDrop: (e: DragEvent) => {
      if (dragKindOf(e) !== 'server') return;
      e.preventDefault();
      const dragged = e.dataTransfer.getData('application/x-clanfire-server');
      const after = isAfter(e, horizontal ? 'x' : 'y');
      setDragId(null);
      setSpot(null);
      if (dragged && dragged !== id) void moveServer(dragged, id, after);
    },
  });
  const dragProps = (id: string) => ({
    draggable: true,
    onDragStart: (e: DragEvent) => {
      startDrag(e, 'server', id);
      setDragId(id);
    },
    onDragEnd: () => {
      setDragId(null);
      setSpot(null);
    },
  });

  return (
    <Horizontal.Provider value={horizontal}>
      <nav
        aria-label="Servers"
        data-cf="server-list"
        className={cn(
          'flex shrink-0 items-center gap-3 bg-rail [scrollbar-width:none]',
          horizontal ? 'h-[72px] w-full flex-row overflow-x-auto px-3' : 'w-20 flex-col overflow-y-auto py-4',
        )}
      >
        <RailSlot pill={isHome ? 'active' : 'none'}>
          <Tooltip label="Direct Messages" placement={tooltip}>
            <Link
              to="/app/@me"
              aria-label="Direct messages"
              aria-current={isHome ? 'page' : undefined}
              className="relative flex h-12 w-12 items-center justify-center rounded-full transition-transform duration-200 hover:scale-105"
            >
              <Logo size={48} />
              <CountBadge count={dmUnread + pending} className="absolute -right-1 -bottom-1 ring-[3px] ring-rail" />
            </Link>
          </Tooltip>
        </RailSlot>

        <div className={cn('shrink-0 rounded-full bg-rail-muted/35', horizontal ? 'h-7 w-[3px]' : 'h-[3px] w-7')} />

        {order.map((id) => (
          <div key={id} className={cn('relative', horizontal ? 'h-full shrink-0' : 'w-full')} {...dropProps(id)}>
            {spot?.id === id &&
              (horizontal ? (
                <span aria-hidden className={cn('pointer-events-none absolute inset-y-3 z-10 w-0.5 rounded-full bg-accent', spot.after ? '-right-1.5' : '-left-1.5')} />
              ) : (
                <span aria-hidden className={cn('pointer-events-none absolute inset-x-3 z-10 h-0.5 rounded-full bg-accent', spot.after ? '-bottom-1' : '-top-1')} />
              ))}
            <ServerRailItem serverId={id} active={id === activeServerId} dragging={dragId === id} dragProps={dragProps(id)} />
          </div>
        ))}

        <RailSlot pill="none">
          <Tooltip label="Create a server" placement={tooltip}>
            <button
              type="button"
              onClick={() => openModal({ type: 'createServer' })}
              aria-label="Create a server"
              className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-rail-muted/60 text-rail-muted transition-colors duration-200 hover:border-rail-fg hover:text-rail-fg"
            >
              <Plus size={20} />
            </button>
          </Tooltip>
        </RailSlot>
        <RailSlot pill="none">
          <Tooltip label="Join with an invite" placement={tooltip}>
            <button
              type="button"
              onClick={() => openModal({ type: 'joinServer' })}
              aria-label="Join a server"
              className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-rail-muted/60 text-rail-muted transition-colors duration-200 hover:border-rail-fg hover:text-rail-fg"
            >
              <Compass size={20} />
            </button>
          </Tooltip>
        </RailSlot>
      </nav>
    </Horizontal.Provider>
  );
}

function ServerRailItem({ serverId, active, dragging, dragProps }: { serverId: string; active: boolean; dragging: boolean; dragProps: object }) {
  const server = useServers((s) => s.servers[serverId]);
  const lastChannel = useUi((s) => s.lastChannelByServer[serverId]);
  const { unread, mentions } = useServerUnread(serverId);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLAnchorElement>(null);
  const items = useServerMenuItems(serverId);
  const horizontal = useContext(Horizontal);
  if (!server) return null;

  return (
    <RailSlot pill={active ? 'active' : unread ? 'unread' : 'none'}>
      <Tooltip label={server.name} placement={horizontal ? 'bottom' : 'right'}>
        <Link
          ref={ref}
          to={lastChannel ? `/app/${serverId}/${lastChannel}` : `/app/${serverId}`}
          aria-label={`${server.name}${mentions ? `, ${mentions} mentions` : unread ? ', unread messages' : ''}`}
          aria-current={active ? 'page' : undefined}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenu({ x: e.clientX, y: e.clientY });
          }}
          {...dragProps}
          className={cn('relative block rounded-full transition-transform duration-200 hover:scale-105', dragging && 'opacity-40')}
        >
          <ServerIcon server={server} size={48} />
          <CountBadge count={mentions} className="absolute -right-1 -bottom-1 ring-[3px] ring-rail" />
        </Link>
      </Tooltip>
      <Menu open={!!menu} onClose={() => setMenu(null)} anchor={menu ?? { x: 0, y: 0 }} placement="bottom-start" items={items} label={`${server.name} options`} />
    </RailSlot>
  );
}

/** Actions shared by the server rail context menu and the channel sidebar header. */
export function useServerMenuItems(serverId: string): MenuEntry[] {
  const server = useServers((s) => s.servers[serverId]);
  const perms = useMyPermissions(serverId);
  const openModal = useUi((s) => s.openModal);
  const myId = useAuth((s) => s.user?.id);
  const navigate = useNavigate();
  const hasPickableRoles = useServers((s) => Object.values(s.roles).some((r) => r.serverId === serverId && r.selfAssignable && !r.isDefault));
  if (!server) return [];

  const setLevel = async (level: NotificationLevel) => {
    try {
      await serverApi.setNotificationLevel(serverId, level);
      useServers.getState().patchServer(serverId, { me: { ...server.me, notificationLevel: level } });
      toast.success(level === 'ALL' ? 'You will be notified of all messages.' : level === 'MENTIONS' ? 'Only @mentions will notify you.' : 'Server muted.');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  /** Puts one of your Stokes (from Blaze) on this server. */
  const stoke = async () => {
    if (!hasBlazeNow()) return openModal({ type: 'userSettings', section: 'blaze' });
    const next = STOKE_LEVELS.find((l) => l.stokes > server.stokeCount);
    const ok = await confirm({
      title: `Stoke ${server.name}?`,
      body: next
        ? `It has ${server.stokeCount} Stoke${server.stokeCount === 1 ? '' : 's'}. ${next.stokes - server.stokeCount} more and it reaches ${next.name}. You can take a Stoke back any time from Settings → ${BLAZE_NAME}.`
        : `It's already at the top level, ${serverPerks(server.stokeCount).name}. Your Stoke keeps it there if someone takes theirs back.`,
      confirmLabel: 'Stoke it',
    });
    if (!ok) return;
    try {
      const updated = await serverApi.stoke(serverId);
      useServers.getState().patchServer(serverId, { stokeCount: updated.stokeCount, bannerUrl: updated.bannerUrl });
      toast.success(`You stoked ${server.name}!`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const markRead = () => {
    const { channels, servers } = useServers.getState();
    const reads = useReadStates.getState().channels;
    for (const channel of Object.values(channels)) {
      if (channel.serverId !== serverId) continue;
      const read = reads[channel.id];
      if (isChannelUnread(channel, read?.lastReadAt, servers[serverId]!.me.joinedAt) || (read?.mentionCount ?? 0) > 0) {
        acknowledge({ channelId: channel.id });
      }
    }
  };

  const leave = async () => {
    const ok = await confirm({
      title: `Leave ${server.name}?`,
      body: "You won't be able to rejoin unless you're invited again.",
      confirmLabel: 'Leave server',
      danger: true,
    });
    if (!ok) return;
    try {
      await serverApi.leave(serverId);
      useServers.getState().removeServer(serverId);
      navigate('/app/@me');
      toast.success(`You left ${server.name}.`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const level = server.me.notificationLevel;
  const canManage = perms.can('MANAGE_SERVER') || perms.can('MANAGE_ROLES') || perms.can('KICK_MEMBERS') || perms.can('BAN_MEMBERS');
  return [
    { label: 'Mark as read', icon: <CheckCheck size={16} />, onSelect: markRead },
    perms.can('CREATE_INVITES') && { label: 'Invite people', icon: <UserPlus size={16} />, onSelect: () => openModal({ type: 'invite', serverId }) },
    canManage && { label: 'Server settings', icon: <Settings size={16} />, onSelect: () => openModal({ type: 'serverSettings', serverId }) },
    hasPickableRoles && { label: 'Pick your roles', icon: <PickRoles size={16} />, onSelect: () => openModal({ type: 'pickRoles', serverId }) },
    perms.can('MANAGE_CHANNELS') && { label: 'Create channel', icon: <Plus size={16} />, onSelect: () => openModal({ type: 'createChannel', serverId, channelType: 'TEXT' }) },
    !!myId &&
      (perms.can('CHANGE_NICKNAME') || server.ownerId === myId) && { label: 'Change nickname', icon: <PenLine size={16} />, onSelect: () => openModal({ type: 'nickname', serverId, userId: myId }) },
    { label: 'Edit server profile', icon: <UserCircle size={16} />, onSelect: () => openModal({ type: 'serverProfile', serverId }) },
    { label: 'Stoke this server', icon: <Stoke size={16} className="text-[#f97316]" />, onSelect: () => void stoke() },
    perms.can('MANAGE_CHANNELS') && { label: 'Create category', icon: <FolderPlus size={16} />, onSelect: () => openModal({ type: 'category', serverId }) },
    'separator',
    { label: 'All messages', icon: <Bell size={16} />, hint: level === 'ALL' ? '✓' : undefined, onSelect: () => void setLevel('ALL') },
    { label: 'Only @mentions', icon: <Bell size={16} />, hint: level === 'MENTIONS' ? '✓' : undefined, onSelect: () => void setLevel('MENTIONS') },
    { label: 'Mute server', icon: <BellOff size={16} />, hint: level === 'NOTHING' ? '✓' : undefined, onSelect: () => void setLevel('NOTHING') },
    server.ownerId !== myId && 'separator',
    server.ownerId !== myId && { label: 'Leave server', icon: <LogOut size={16} />, danger: true, onSelect: () => void leave() },
  ];
}
